import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import NewsCard from "../components/NewsCard";
import CategoryFilter from "../components/CategoryFilter";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import AdSense from "../components/AdSense";
import BannerAd from "../components/BannerAd";
import AffiliateSection from "../components/AffiliateSection";
import { Loader2 } from "lucide-react";

const Home = () => {
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("Todas");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchCategories();
    fetchNews();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      fetchNews();
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(["Todas", ...response.data.categories]);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchNews = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === "Todas"
        ? `${API}/news` 
        : `${API}/news?category=${selectedCategory}`;
      const response = await axios.get(url);
      setNews(response.data);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query) {
      setSearchQuery("");
      fetchNews();
      return;
    }

    setSearchQuery(query);
    setLoading(true);
    try {
      const response = await axios.get(`${API}/news/search?q=${encodeURIComponent(query)}`);
      setNews(response.data);
    } catch (error) {
      console.error("Error searching news:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Notícias de Finanças Pessoais"
        description="Informações atualizadas diariamente dos sites mais confiáveis do mundo. Fique por dentro do mercado financeiro, investimentos, economia e criptomoedas."
        keywords="finance news, personal finance, investments, economy, cryptocurrency, stock market, financial news"
      />
      <Header onSearch={handleSearch} />
      
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Hero Section */}
        <div className="mb-16">
          <h1 
            className="text-5xl md:text-7xl font-bold tracking-tight leading-none text-slate-900 mb-6"
            data-testid="hero-title"
          >
            Notícias de Finanças Pessoais
          </h1>
          <p 
            className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-3xl"
            data-testid="hero-description"
          >
            Informações atualizadas diariamente dos sites mais confiáveis do mundo. Fique por dentro do mercado financeiro, investimentos, economia e criptomoedas.
          </p>
        </div>

        {/* Category Filter */}
        {!searchQuery && (
          <CategoryFilter 
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        )}

        {/* Search Results Info */}
        {searchQuery && (
          <div className="mb-8">
            <p className="text-lg text-slate-600">
              Resultados da busca por: <strong>"{searchQuery}"</strong>
            </p>
            <button
              onClick={() => handleSearch("")}
              className="text-emerald-900 hover:underline mt-2"
            >
              Limpar busca
            </button>
          </div>
        )}

        {/* Top Banner Ad */}
        <BannerAd size="large" />

        {/* Affiliate Section */}
        <AffiliateSection />

        {/* News Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20" data-testid="loading-indicator">
            <Loader2 className="w-12 h-12 animate-spin text-emerald-900" />
          </div>
        ) : (
          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            data-testid="news-grid"
          >
            {news.map((article, index) => (
              <div key={article.id} className="contents">
                <NewsCard article={article} />
                {/* AdSense between articles (every 6 articles) */}
                {(index + 1) % 6 === 0 && (
                  <div className="col-span-1 md:col-span-2 lg:col-span-3">
                    <AdSense 
                      adSlot="1234567890"
                      adFormat="horizontal"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && news.length === 0 && (
          <div className="text-center py-20" data-testid="no-news-message">
            <p className="text-xl text-slate-600">Nenhuma notícia encontrada.</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Home;